import React, { useEffect, useState } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Typography from '@mui/material/Typography';
import LgmButton from '../../LgmButton';
import { useModalBackDismiss } from 'navigation/useModalBackDismiss';
import {
  useSearchManufacturerLinkCandidates,
  useManualLinkManufacturer,
} from '../../../services/api/domains/manufacturers/hooks';
import { ManufacturerLinkCandidate } from '../../../services/api/domains/manufacturers/types';
import { DEBOUNCE_DELAY } from '../../../shared/hooks/useAsyncSelect';
import {
  GRAY_200,
  GRAY_400,
  GRAY_500,
  GRAY_50,
  GRAY_900,
} from 'theme/grayScale';
import { COLOR_DANGER_TEXT, COLOR_INFO } from 'theme/colors';

export interface LinkToSourceModalProps {
  manufacturerId: number;
  /** Source key, e.g. 'decrs' | 'gleif' | 'sap_aml' — any `connected` source with a `searchColumn`. */
  source: string;
  sourceLabel: string;
  onClose: () => void;
  onLinked: () => void;
}

/**
 * Shared "Link to Source" search modal for manufacturers (MFGV2-003,
 * Sub-task 3.2). One component handles search + link for any connected
 * manufacturer source — driven entirely by the `source` prop, mirroring the
 * product feature's ExternalLinkSearchPane/LinkConfirmDialog pattern at a
 * smaller scale (manufacturer linking has no multi-record "confirm many"
 * step — one candidate, one link).
 *
 * A new component rather than generalizing the existing product
 * LinkToSourceModal: that component is hardcoded to `productId` throughout
 * (Sub-task 3.1's Architecture Decision) — parallel, source-driven
 * components avoid any risk to the existing product-linking feature.
 *
 * Opened from the manufacturer modal's Details tab, which itself renders
 * inside the app's custom Product/Manufacturer modal overlay (z-index
 * 10000 — see client/src/components/TableSearch/AsyncSearchSelect.tsx's own
 * comment on this). Without a z-index override, MUI's default Dialog
 * z-index (1300) renders this dialog fully behind that overlay — present in
 * the DOM and accessibility tree, but visually invisible and unclickable
 * (confirmed via manual QA, 2026-09-01). `zIndex: 20000` matches the tier
 * this codebase already reserves for a dialog nested above a level-2 modal
 * (BreakLinkConfirmDialog/ConflictModal/MultiResultModal in the product
 * feature all use this same value for the same reason).
 */
const LinkToSourceModal: React.FC<LinkToSourceModalProps> = ({
  manufacturerId,
  source,
  sourceLabel,
  onClose,
  onLinked,
}) => {
  const searchCandidates = useSearchManufacturerLinkCandidates();
  const linkMutation = useManualLinkManufacturer();

  const [query, setQuery] = useState('');
  const [results, setResults] = useState<ManufacturerLinkCandidate[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [selected, setSelected] = useState<ManufacturerLinkCandidate | null>(
    null
  );
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const isLinking = linkMutation.status === 'loading';

  useModalBackDismiss({ isOpen: true, onClose, canDismiss: () => !isLinking });

  useEffect(() => {
    const trimmed = query.trim();
    if (trimmed.length < 2) {
      setResults([]);
      setIsSearching(false);
      return;
    }
    let cancelled = false;
    setIsSearching(true);
    const timer = setTimeout(async () => {
      try {
        const candidates = await searchCandidates(
          source,
          trimmed,
          manufacturerId
        );
        if (!cancelled) {
          setResults(candidates);
          setErrorMessage(null);
        }
      } catch {
        if (!cancelled) {
          setResults([]);
          setErrorMessage('Search failed. Try again.');
        }
      } finally {
        if (!cancelled) {
          setIsSearching(false);
        }
      }
    }, DEBOUNCE_DELAY);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [query, source, manufacturerId, searchCandidates]);

  const handleLink = async () => {
    if (!selected) return;
    setErrorMessage(null);
    try {
      await linkMutation.mutate({
        manufacturerId,
        source,
        sourceRecordId: selected.id,
      });
      onLinked();
    } catch {
      setErrorMessage('Failed to create the link. Try again.');
    }
  };

  return (
    <Dialog
      open
      onClose={isLinking ? undefined : () => onClose()}
      maxWidth="sm"
      fullWidth
      sx={{ zIndex: 20000 }}>
      <DialogTitle>Link to {sourceLabel}</DialogTitle>
      <DialogContent>
        <input
          type="text"
          autoFocus
          placeholder={`Search ${sourceLabel} by name...`}
          value={query}
          onChange={e => {
            setQuery(e.target.value);
            setSelected(null);
            setErrorMessage(null);
          }}
          style={{
            width: '100%',
            padding: '8px 10px',
            fontSize: '0.875rem',
            border: `1px solid ${GRAY_200}`,
            borderRadius: '4px',
            marginBottom: '10px',
            boxSizing: 'border-box',
          }}
        />

        {isSearching && (
          <Typography variant="body2" style={{ color: GRAY_500 }}>
            Searching...
          </Typography>
        )}

        {!isSearching && query.trim().length >= 2 && results.length === 0 && (
          <Typography
            variant="body2"
            style={{ color: GRAY_400, fontStyle: 'italic' }}>
            No matching {sourceLabel} records found
          </Typography>
        )}

        {!isSearching && results.length > 0 && (
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '4px',
              maxHeight: '280px',
              overflowY: 'auto',
            }}>
            {results.map(candidate => {
              const isSelected = selected?.id === candidate.id;
              const disabled = candidate.alreadyLinked;
              return (
                <div
                  key={candidate.id}
                  role="button"
                  aria-disabled={disabled}
                  tabIndex={disabled ? -1 : 0}
                  onClick={disabled ? undefined : () => setSelected(candidate)}
                  onKeyDown={
                    disabled
                      ? undefined
                      : e => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            setSelected(candidate);
                          }
                        }
                  }
                  style={{
                    padding: '8px 10px',
                    borderRadius: '4px',
                    cursor: disabled ? 'not-allowed' : 'pointer',
                    backgroundColor: disabled
                      ? GRAY_50
                      : isSelected
                        ? GRAY_50
                        : 'transparent',
                    border: `1px solid ${isSelected && !disabled ? COLOR_INFO : GRAY_200}`,
                    opacity: disabled ? 0.6 : 1,
                  }}>
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      gap: '8px',
                    }}>
                    <div
                      style={{
                        fontSize: '0.8125rem',
                        color: GRAY_900,
                        fontWeight: 500,
                      }}>
                      {candidate.displayName}
                    </div>
                    {disabled && (
                      <div
                        style={{
                          fontSize: '0.6875rem',
                          fontWeight: 700,
                          textTransform: 'uppercase',
                          letterSpacing: '0.04em',
                          color: GRAY_500,
                        }}>
                        Already linked
                      </div>
                    )}
                  </div>
                  <div style={{ fontSize: '0.75rem', color: GRAY_500 }}>
                    {candidate.naturalKey}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {errorMessage && (
          <Typography
            variant="body2"
            style={{ color: COLOR_DANGER_TEXT, marginTop: '8px' }}>
            {errorMessage}
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        <LgmButton variant="flat" onClick={onClose} disabled={isLinking}>
          Cancel
        </LgmButton>
        <LgmButton
          variant="contained"
          color="primary"
          onClick={handleLink}
          disabled={!selected || isLinking}>
          {isLinking ? 'Linking...' : 'Link'}
        </LgmButton>
      </DialogActions>
    </Dialog>
  );
};

export default LinkToSourceModal;
